import { Injectable, Inject } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, of, from } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { OktaAuthGuard } from '@okta/okta-angular';
import { UrlKey, UrlService } from '../services/url/url.service';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '../environment.config';

/**
 * Guard that bypasses Okta authentication when the rkn-enabled gatekeeper toggle is on.
 * When RKN is enabled, Kong handles authentication and validates the required Okta group,
 * so the Angular app does not need to enforce Okta auth on routes.
 * Falls back to OktaAuthGuard when rkn-enabled is false or the gatekeeper call fails.
 * TODO: Okta guard and this RKN guard can be deleted once fully migrated to Kong
 */
@Injectable()
export class RknAuthGuard implements CanActivate {
  private rknEnabled$: Observable<boolean> | null = null;

  constructor(
    private httpClient: HttpClient,
    private urlService: UrlService,
    @Inject(ENVIRONMENT_CONFIG) private config: EnvironmentConfig,
    private oktaAuthGuard: OktaAuthGuard
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.getRknEnabled().pipe(
      switchMap(rknEnabled => {
        if (rknEnabled) {
          return of(true);
        }
        return from(this.oktaAuthGuard.canActivate(route, state));
      })
    );
  }

  private getRknEnabled(): Observable<boolean> {
    if (!this.rknEnabled$) {
      const gatekeeperUrl = this.urlService.getUrl(UrlKey.gatekeeper_url, {
        featureIds: 'rkn-enabled',
        env: this.config.gatekeeper_env
      });

      this.rknEnabled$ = this.httpClient
        .get<{ featureId: string; enabled: boolean }[]>(gatekeeperUrl)
        .pipe(
          map(features => features.find(f => f.featureId === 'rkn-enabled')?.enabled ?? false),
          catchError(() => of(false)),
          shareReplay(1)
        );
    }

    return this.rknEnabled$;
  }
}
